export type MediaItem = { name: string; url: string; size: number; mtime: number; mime?: string | null; width?: number | null; height?: number | null; tags?: string[]; description?: string };

const BASES = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
  ? ['http://localhost:8000']
  : [''];

type Attempt = { base: string; ok: boolean; status?: number; message?: string };

function normalizeItemUrl(item: MediaItem, base: string): MediaItem {
  const u = String(item.url || '');
  if (u.startsWith('http')) return item;
  if (base && u.startsWith('/')) return { ...item, url: `${base}${u}` };
  return item;
}

function fetchWithTimeout(input: string, init?: RequestInit, timeoutMs: number = 15000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  const merged: RequestInit = { ...(init || {}), signal: controller.signal };
  return fetch(input, merged).finally(() => clearTimeout(id));
}

export async function countMedia(): Promise<number> {
  let lastErr: any = null;
  for (const b of BASES) {
    try {
      const url = `${b}/count_media.php`;
      const res = await fetchWithTimeout(url, { cache: 'no-store' });
      if (!res.ok) { lastErr = new Error(`HTTP ${res.status}`); continue; }
      const data = await res.json();
      if (typeof data?.count === 'number') return data.count;
    } catch (e) {
      lastErr = e;
      continue;
    }
  }
  throw lastErr || new Error('Failed to count media');
}

async function tryFetch(path: string, init?: RequestInit) {
  let last: any = null;
  for (const b of BASES) {
    try {
      const url = b ? `${b}${path}` : path;
      const res = await fetchWithTimeout(url, init);
      if (res.ok) return res;
      last = new Error(`HTTP ${res.status}`);
    } catch (e) {
      last = e;
    }
  }
  throw last || new Error('Network error');
}

export async function listMedia(): Promise<MediaItem[]> {
  let lastErr: any = null;
  for (const b of BASES) {
    try {
      const url = `${b}/get_media.php`;
      const res = await fetchWithTimeout(url, { cache: 'no-store' });
      if (!res.ok) { lastErr = new Error(`HTTP ${res.status}`); continue; }
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) { lastErr = new Error('Non-JSON response'); continue; }
      const data = await res.json();
      const items = (data.items ?? []) as MediaItem[];
      return items.map((it) => normalizeItemUrl(it, b));
    } catch (e) {
      lastErr = e;
      continue;
    }
  }
  throw lastErr || new Error('Failed to load media');
}

export async function uploadMedia(file: File): Promise<MediaItem> {
  const form = new FormData();
  form.append('file', file);
  let lastMessage: string | null = null;
  const attempts: Attempt[] = [];
  for (const b of BASES) {
    try {
      const url = b ? `${b}/upload_media.php` : '/upload_media.php';
      const res = await fetchWithTimeout(url, { method: 'POST', body: form });
      if (!res.ok) {
        try {
          const err = await res.json();
          if (err && err.error) {
            lastMessage = String(err.error);
            attempts.push({ base: b, ok: false, status: res.status, message: lastMessage });
            continue;
          }
        } catch {
          try {
            const txt = await res.text();
            if (txt) lastMessage = `HTTP ${res.status}: ${txt.slice(0, 120)}...`;
          } catch {}
        }
        attempts.push({ base: b, ok: false, status: res.status, message: lastMessage || `HTTP ${res.status}` });
        continue;
      }
      const data = await res.json();
      attempts.push({ base: b, ok: true });
      return normalizeItemUrl(data.item as MediaItem, b);
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        lastMessage = 'Request timed out';
        attempts.push({ base: b, ok: false, message: 'timeout' });
      } else {
        if (e?.message) lastMessage = e.message;
        attempts.push({ base: b, ok: false, message: lastMessage || 'network error' });
      }
    }
  }
  const detail = attempts.map(a => `${a.base} => ${a.ok ? 'ok' : (a.status ? `HTTP ${a.status}` : (a.message || 'error'))}`).join('; ');
  throw new Error((lastMessage || 'Upload failed') + ` (${detail})`);
}

export async function deleteMedia(name: string): Promise<boolean> {
  for (const b of BASES) {
    try {
      const url = b ? `${b}/delete_media.php` : '/delete_media.php';
      const res = await fetchWithTimeout(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
      if (!res.ok) {
        try {
          const err = await res.json();
          if (err && err.error) throw new Error(err.error);
        } catch {}
        continue;
      }
      return true;
    } catch {}
  }
  return false;
}

export async function saveMediaMeta(name: string, tags: string[], description: string): Promise<boolean> {
  const payload = { name, tags, description };
  for (const b of BASES) {
    try {
      const url = `${b}/save_media.php`;
      const res = await fetchWithTimeout(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) {
        try { const err = await res.json(); if (err?.error) throw new Error(err.error); } catch {}
        continue;
      }
      return true;
    } catch {}
  }
  return false;
}

export async function checkMediaHealth(): Promise<{ base: string; info: any } | null> {
  let last: any = null;
  for (const b of BASES) {
    try {
      const url = `${b}/health_media.php`;
      const res = await fetchWithTimeout(url, { cache: 'no-store' });
      if (!res.ok) { last = new Error(`HTTP ${res.status}`); continue; }
      const data = await res.json();
      return { base: b, info: data };
    } catch (e) {
      last = e;
    }
  }
  if (last) throw last;
  return null;
}

export async function savePrefab(payload: { fileName: string; displayName?: string; component: string; props: any; notes?: string }): Promise<MediaItem> {
  let lastErr: any = null;
  for (const b of BASES) {
    try {
      const url = `${b}/save_prefab.php`;
      const res = await fetchWithTimeout(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) {
        try { const err = await res.json(); if (err?.error) throw new Error(err.error); } catch (e) {}
        continue;
      }
      const data = await res.json();
      if (data && data.item) return data.item as MediaItem;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Failed to save prefab');
}

export async function checkPrefabExists(fileName: string): Promise<{ exists: boolean; path?: string }> {
  let lastErr: any = null;
  for (const b of BASES) {
    try {
      const url = `${b}/check_prefab.php`;
      const res = await fetchWithTimeout(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fileName }) });
      if (!res.ok) continue;
      const data = await res.json();
      return { exists: !!data?.exists, path: data?.path };
    } catch (e) {
      lastErr = e;
    }
  }
  if (lastErr) throw lastErr;
  return { exists: false };
}
